import type { CityConfig } from "../city-template/types";

export const murrayConfig: CityConfig = {
  city: "Murray",
  state: "Kentucky",
  stateAbbr: "KY",
  slug: "murray",
  route: "/cities/murray",
  title: "Emergency Passport Murray, KY | Fast Passport Renewal | Calloway County",
  metaDescription: "Emergency passport and expedited passport services in Murray, KY — as fast as 24 hours. Fast passport renewal and child passport expediting for Calloway County and western Kentucky travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency \u0026 Expedited Passport Services in Murray, KY",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Murray and western Kentucky travelers get their expedited passports as quickly as 24 hours. Serving Calloway County, Paducah, Mayfield, and Lake Barkley. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Murray%20Kentucky%20western%20Kentucky%20scenic%20landscape%20at%20golden%20hour%20with%20Murray%20State%20University%20campus%20buildings%2C%20Kentucky%20Lake%20and%20Land%20Between%20the%20Lakes%20in%20the%20distance%2C%20charming%20southern%20town%20with%20tree-lined%20streets%20and%20historic%20downtown%20buildings%2C%20warm%20rural%20Kentucky%20sunset%20light%2C%20no%20visible%20text%20or%20people%2C%20wide%20panoramic%20view&width=1800&height=700&seq=murray-hero-001&orientation=landscape",
  heroImageAlt: "Murray Kentucky with Murray State University campus and western Kentucky Lake Barkley landscape",
  geoPosition: "36.6101;-88.3147",
  geoPlacename: "Murray, Kentucky",
  regionalAgency: {
    name: "Atlanta Passport Agency",
    address: "230 Peachtree St NW, Suite 1000, Atlanta, GA 30308",
    distance: "Located in downtown Atlanta — approximately 375 miles from Murray via I-24",
  },
  nearbyCitiesZips: [
    { name: "Murray", zips: "42071" },
    { name: "Mayfield", zips: "42066" },
    { name: "Benton", zips: "42025" },
    { name: "Cadiz", zips: "42211" },
    { name: "Paris (TN)", zips: "38242" },
    { name: "Paducah", zips: "42001, 42002, 42003" },
    { name: "Calvert City", zips: "42029" },
    { name: "Princeton", zips: "42445" },
    { name: "Hopkinsville", zips: "42240, 42241" },
    { name: "Fulton", zips: "42041" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Calloway County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Paducah", route: "/cities/paducah" },
    { name: "Fast passport renewal in Hopkinsville", route: "/cities/hopkinsville" },
    { name: "Emergency passport help in Bowling Green", route: "/cities/bowling-green" },
    { name: "Urgent passport services in Owensboro", route: "/cities/owensboro" },
    { name: "Same-day passport help in Henderson", route: "/cities/henderson" },
    { name: "Passport courier services in Somerset", route: "/cities/somerset" },
  ],
  fpcOfficeAddress: "500 W Jefferson St, Louisville, KY 40202",
  fpcOfficeNote: "Fast Passport Center serves Murray remotely via secure courier. We do not have a walk-in office in Murray — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Calloway County travelers.",
  fpcOfficeDistance: "Serving all of Calloway County and western Kentucky",
  serviceAreaIntro: "From Murray State University campus to the shores of Kentucky Lake, from downtown Murray to the Land Between the Lakes — Fast Passport Center serves the entire Murray community and western Kentucky. We regularly help travelers from Mayfield, Benton, and Paducah get their passports fast.",
  nearbyCitiesIntro: "We serve Murray and all surrounding western Kentucky cities including Mayfield, Benton, Cadiz, Paris (TN), Paducah, Calvert City, Princeton, Hopkinsville, and Fulton. If you are in Calloway County, western Kentucky, or the Kentucky Lake region, we have you covered.",
  howItWorksIntro: "Most Murray travelers complete everything online and ship documents overnight. No need to drive to Atlanta or wait in line at the regional agency.",
  mistakesIntro: "Murray and Calloway County travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Murray and western Kentucky travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Murray? Here are the most common ones we hear from Calloway County travelers.",
  statePageRoute: "/states/kentucky",
  statePageName: "Kentucky",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload \u0026 wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Calloway County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Laura S.",
      location: "Murray, KY",
      rating: 5,
      text: "Murray State professor — needed a passport renewed for an international conference in Germany with only a week's notice. They delivered in 4 days. Absolutely outstanding.",
      service: "Passport Renewal",
    },
    {
      name: "Travis K.",
      location: "Mayfield, KY",
      rating: 5,
      text: "Emergency trip to visit family in Mexico after a health crisis. Fast Passport Center got me an emergency passport in under 48 hours. I'm so grateful.",
      service: "Emergency Passport",
    },
    {
      name: "Nancy W.",
      location: "Benton, KY",
      rating: 5,
      text: "First passport for my son before a summer exchange program to France. They guided us through every step from our small western Kentucky town.",
      service: "Child Passport",
    },
    {
      name: "George B.",
      location: "Murray, KY",
      rating: 5,
      text: "Had a last-minute Caribbean cruise booked and my passport was expired. They renewed it in 5 days and we sailed right on schedule.",
      service: "Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "murray-1",
      name: "Murray Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "500 Main St",
      city: "Murray",
      state: "KY",
      zip: "42071",
      phone: "(270) 753-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Murray",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Calloway County. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "murray-2",
      name: "Paducah Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "400 Joe Clifton Dr",
      city: "Paducah",
      state: "KY",
      zip: "42001",
      phone: "(270) 444-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Paducah",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Largest facility in western Kentucky. High volume — book appointments early.",
      icon: "ri-mail-line",
    },
    {
      id: "murray-3",
      name: "Mayfield Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "501 E Broadway",
      city: "Mayfield",
      state: "KY",
      zip: "42066",
      phone: "(270) 247-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Mayfield",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Graves County. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "murray-4",
      name: "Calloway County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "101 S 5th St",
      city: "Murray",
      state: "KY",
      zip: "42071",
      phone: "(270) 753-3923",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.2 miles from Murray Courthouse",
      website: "https://www.callowaycounty.ky.gov/",
      websiteLabel: "Calloway County Clerk",
      notes: "County-level passport acceptance. Appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 6 hour drive to Atlanta", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Calloway County photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Calloway County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Murray applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Atlanta regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Calloway County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Murray travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Murray or Paducah." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Murray address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Murray before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Murray for travelers with urgent international flights. Our registered couriers hand-deliver documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 6 hour drive to Atlanta." },
    { q: "How fast can I renew my passport in Murray?", a: "Fast Passport Center offers fast passport renewal in Murray in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "Where are the closest passport acceptance facilities in Murray?", a: "Popular options include the Murray Post Office (42071), Calloway County Clerk (42071), Paducah Main Post Office (42001), and Mayfield Post Office (42066). All require appointments. We help you find the most convenient location." },
    { q: "Can I expedite a child passport in Murray?", a: "Yes. Fast Passport Center expedites child passports in Murray for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "Do I need to drive to the Atlanta Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities." },
    { q: "How much does expedited passport service cost in Murray?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed. Contact us for a personalized quote." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Murray", address: "103 N 12th St, Murray, KY 42071", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Murray", address: "809 N 12th St, Murray, KY 42071", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Paducah", address: "3420 Park Ave, Paducah, KY 42001", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart — Paducah", address: "5101 Hinkleville Rd, Paducah, KY 42001", hours: "Mon–Sun: 6AM–11PM" },
  ],
  dmvLocations: [
    {
      name: "Circuit Court Clerk — Calloway County",
      address: "101 S 5th St, Murray, KY 42071",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Driver's license and REAL ID. Appointment recommended.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Graves County",
      address: "100 E Broadway, Mayfield, KY 42066",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals. Online booking available.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PAH",
      name: "Barkley Regional Airport (Paducah)",
      distance: "40 miles from Murray",
      address: "1790 Barkley Dr, Paducah, KY 42001",
      note: "Closest commercial airport. Limited regional flights with connections to major hubs.",
      flightUrl: "https://www.barkleyregional.com/",
      flightLabel: "Barkley Regional Airport",
      featured: true,
    },
    {
      code: "BNA",
      name: "Nashville International Airport",
      distance: "125 miles from Murray",
      address: "1 Terminal Dr, Nashville, TN 37214",
      note: "Major international hub accessible via I-24. International flights to Europe, Caribbean, and beyond.",
      flightUrl: "https://www.flynashville.com/",
      flightLabel: "BNA Airport",
      featured: false,
    },
  ],
};